/**
 * Smart alignment guides + snapping (Figma-style helper lines).
 *
 * Pure, framework-agnostic implementation of the well-known React Flow
 * "helper lines" recipe. Given a single position change for the dragged node
 * and the current set of nodes, it finds the nearest vertical and horizontal
 * alignment guides (within SNAP_DISTANCE) and returns the snapped top-left
 * position along with the guide coordinates (in flow coordinates).
 */

/** Default snap distance in flow coordinates. */
export const SNAP_DISTANCE = 5

/** Minimal node shape this utility relies on. */
export interface HelperLineNode {
  id: string
  position: { x: number; y: number }
  width?: number | null
  height?: number | null
  measured?: { width?: number | null; height?: number | null }
}

/** Minimal position change shape (a single React Flow position NodeChange). */
export interface HelperLinePositionChange {
  id: string
  position?: { x: number; y: number }
  [key: string]: unknown
}

export interface GetHelperLinesResult {
  horizontal?: number
  vertical?: number
  snapPosition: { x: number; y: number }
}

/** Resolve a node's width, falling back through measured -> default. */
function nodeWidth(node: HelperLineNode, fallback = 120): number {
  return node.width ?? node.measured?.width ?? fallback
}

/** Resolve a node's height, falling back through measured -> default. */
function nodeHeight(node: HelperLineNode, fallback = 120): number {
  return node.height ?? node.measured?.height ?? fallback
}

/**
 * Compute the nearest vertical/horizontal alignment guides for a dragged node.
 *
 * Matches the dragged node's left/center/right edges against every other
 * node's left/center/right edges (vertical guides) and its top/middle/bottom
 * edges against every other node's top/middle/bottom (horizontal guides).
 *
 * @param change   The single position change for the dragged node.
 * @param nodes    The current node list.
 * @param distance Snap distance in flow coordinates (default SNAP_DISTANCE).
 */
export function getHelperLines(
  change: HelperLinePositionChange,
  nodes: HelperLineNode[],
  distance: number = SNAP_DISTANCE
): GetHelperLinesResult {
  const defaultResult: GetHelperLinesResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: change.position?.x ?? 0, y: change.position?.y ?? 0 },
  }

  if (!change.position) {
    return defaultResult
  }

  const dragged = nodes.find((n) => n.id === change.id)
  if (!dragged) {
    return defaultResult
  }

  const draggedWidth = nodeWidth(dragged)
  const draggedHeight = nodeHeight(dragged)

  // Edges of the dragged node at its proposed position.
  const draggedLeft = change.position.x
  const draggedRight = change.position.x + draggedWidth
  const draggedCenterX = change.position.x + draggedWidth / 2

  const draggedTop = change.position.y
  const draggedBottom = change.position.y + draggedHeight
  const draggedCenterY = change.position.y + draggedHeight / 2

  // Track the closest match for each axis.
  let verticalDistance = distance
  let horizontalDistance = distance

  return nodes.reduce<GetHelperLinesResult>((result, other) => {
    if (other.id === dragged.id) {
      return result
    }

    const otherWidth = nodeWidth(other)
    const otherHeight = nodeHeight(other)

    const otherLeft = other.position.x
    const otherRight = other.position.x + otherWidth
    const otherCenterX = other.position.x + otherWidth / 2

    const otherTop = other.position.y
    const otherBottom = other.position.y + otherHeight
    const otherCenterY = other.position.y + otherHeight / 2

    // ---- Vertical guides (X axis) ----

    // left -> left
    const distLeftLeft = Math.abs(draggedLeft - otherLeft)
    if (distLeftLeft < verticalDistance) {
      result.snapPosition.x = otherLeft
      result.vertical = otherLeft
      verticalDistance = distLeftLeft
    }

    // right -> right
    const distRightRight = Math.abs(draggedRight - otherRight)
    if (distRightRight < verticalDistance) {
      result.snapPosition.x = otherRight - draggedWidth
      result.vertical = otherRight
      verticalDistance = distRightRight
    }

    // left -> right
    const distLeftRight = Math.abs(draggedLeft - otherRight)
    if (distLeftRight < verticalDistance) {
      result.snapPosition.x = otherRight
      result.vertical = otherRight
      verticalDistance = distLeftRight
    }

    // right -> left
    const distRightLeft = Math.abs(draggedRight - otherLeft)
    if (distRightLeft < verticalDistance) {
      result.snapPosition.x = otherLeft - draggedWidth
      result.vertical = otherLeft
      verticalDistance = distRightLeft
    }

    // center -> center
    const distCenterX = Math.abs(draggedCenterX - otherCenterX)
    if (distCenterX < verticalDistance) {
      result.snapPosition.x = otherCenterX - draggedWidth / 2
      result.vertical = otherCenterX
      verticalDistance = distCenterX
    }

    // ---- Horizontal guides (Y axis) ----

    // top -> top
    const distTopTop = Math.abs(draggedTop - otherTop)
    if (distTopTop < horizontalDistance) {
      result.snapPosition.y = otherTop
      result.horizontal = otherTop
      horizontalDistance = distTopTop
    }

    // bottom -> bottom
    const distBottomBottom = Math.abs(draggedBottom - otherBottom)
    if (distBottomBottom < horizontalDistance) {
      result.snapPosition.y = otherBottom - draggedHeight
      result.horizontal = otherBottom
      horizontalDistance = distBottomBottom
    }

    // top -> bottom
    const distTopBottom = Math.abs(draggedTop - otherBottom)
    if (distTopBottom < horizontalDistance) {
      result.snapPosition.y = otherBottom
      result.horizontal = otherBottom
      horizontalDistance = distTopBottom
    }

    // bottom -> top
    const distBottomTop = Math.abs(draggedBottom - otherTop)
    if (distBottomTop < horizontalDistance) {
      result.snapPosition.y = otherTop - draggedHeight
      result.horizontal = otherTop
      horizontalDistance = distBottomTop
    }

    // middle -> middle
    const distCenterY = Math.abs(draggedCenterY - otherCenterY)
    if (distCenterY < horizontalDistance) {
      result.snapPosition.y = otherCenterY - draggedHeight / 2
      result.horizontal = otherCenterY
      horizontalDistance = distCenterY
    }

    return result
  }, defaultResult)
}
